// src/utils/outreach-api.js — REST helpers for the Gmail outreach pipeline.
// Attaches the Firebase ID token (same pattern as chat-history-api).
import { auth } from '../firebase/firebaseConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

async function authHeaders() {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const idToken = await user.getIdToken();
  return { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' };
}

async function request(path, options = {}) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE_URL}/api/outreach${path}`, { ...options, headers });
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = { error: text }; }
  if (!res.ok) {
    const msg = (data && (data.error || data.detail)) || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

export function draftOutreach({ candidates, roleQuery, roleTitle, recruiter, campaignId, instruction }) {
  return request('/draft', { method: 'POST', body: JSON.stringify({ candidates, roleQuery, roleTitle, recruiter, campaignId, instruction }) });
}

// Re-derive a ranked, email-bearing shortlist from a role query (search → outreach).
export function fetchShortlist(roleQuery, limit = 10, requireEmail = true) {
  return request('/shortlist', { method: 'POST', body: JSON.stringify({ roleQuery, limit, requireEmail }) });
}

export function listOutreach(status) {
  return request(status ? `?status=${encodeURIComponent(status)}` : '', { method: 'GET' });
}

export function getStats() {
  return request('/stats', { method: 'GET' });
}

export function getOutreach(id) {
  return request(`/${id}`, { method: 'GET' });
}

export function updateOutreach(id, patch) {
  return request(`/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export function createGmailDraft(id, googleAccessToken, recruiter) {
  return request(`/${id}/gmail-draft`, { method: 'POST', body: JSON.stringify({ googleAccessToken, recruiter }) });
}

export function sendOutreach(id, googleAccessToken, recruiter) {
  return request(`/${id}/send`, { method: 'POST', body: JSON.stringify({ googleAccessToken, recruiter }) });
}

export function followUp(id, { googleAccessToken, send, recruiter }) {
  return request(`/${id}/follow-up`, { method: 'POST', body: JSON.stringify({ googleAccessToken, send, recruiter }) });
}

export function syncReplies(googleAccessToken) {
  return request('/sync-replies', { method: 'POST', body: JSON.stringify({ googleAccessToken }) });
}

// --- Do-not-contact / suppression list ---
export function listSuppression() {
  return request('/suppression', { method: 'GET' });
}

export function addSuppression(email, note) {
  return request('/suppression', { method: 'POST', body: JSON.stringify({ email, note }) });
}

export function removeSuppression(email) {
  return request(`/suppression/${encodeURIComponent(email)}`, { method: 'DELETE' });
}

// --- Background sync (server-side OAuth + scheduler) ---
export function backgroundStatus() {
  return request('/gmail/status', { method: 'GET' });
}

export function backgroundAuthUrl() {
  return request('/gmail/auth-url', { method: 'GET' });
}

// Save the user's own Google OAuth client (bring-your-own-app). Secret stored encrypted server-side.
export function saveBackgroundOAuthConfig({ clientId, clientSecret }) {
  return request('/gmail/oauth-config', { method: 'POST', body: JSON.stringify({ clientId, clientSecret }) });
}

export function updateBackgroundSettings(settings) {
  return request('/gmail/settings', { method: 'PATCH', body: JSON.stringify(settings) });
}

export function disconnectBackground() {
  return request('/gmail/disconnect', { method: 'DELETE' });
}

// Force a background pass right now (sync replies + send queue + follow-ups).
export function runBackgroundNow() {
  return request('/gmail/run-now', { method: 'POST' });
}

// --- Meeting scheduling (propose-times → confirm → book on the connected calendar) ---
export function proposeMeeting(id, settings = {}) {
  return request(`/${id}/propose-meeting`, { method: 'POST', body: JSON.stringify(settings || {}) });
}

export function sendMeetingProposal(id, { googleAccessToken, subject, body, recruiter } = {}) {
  return request(`/${id}/send-proposal`, { method: 'POST', body: JSON.stringify({ googleAccessToken, subject, body, recruiter }) });
}

export function bookMeeting(id, { slotIndex, recruiter } = {}) {
  return request(`/${id}/book-meeting`, { method: 'POST', body: JSON.stringify({ slotIndex, recruiter }) });
}

export function queueOutreach(id, { scheduledFor, recruiter } = {}) {
  return request(`/${id}/queue`, { method: 'POST', body: JSON.stringify({ scheduledFor, recruiter }) });
}

export function queueApproved({ recruiter, spacingMinutes } = {}) {
  return request('/queue-approved', { method: 'POST', body: JSON.stringify({ recruiter, spacingMinutes }) });
}

export { API_BASE_URL };
